// Keep roughly one poll in reserve instead of draining each response as a burst.
const START_BUFFER_MS = 600;
const MIN_BUFFER_SECONDS = 0.75;
// Bootstrap only until two prefixes arrive. Dev Sol-low probes measured roughly
// 420–470 chars/s in English; every live stream replaces this estimate itself.
const INITIAL_CHARS_PER_SECOND = 400;
const MAX_FINISH_MS = 1500;

/** One playback clock for full-text streams and sparse edit-prefix streams. */
export class ForwardTextReveal {
    private target = '';
    private visible = '';
    private timer: ReturnType<typeof setTimeout> | null = null;
    private bufferUntil: number | null = null;
    private lastTick = 0;
    private credit = 0;
    private rate = 0;
    private incomingRate = INITIAL_CHARS_PER_SECOND;
    private reserveSeconds = MIN_BUFFER_SECONDS;
    private arrivals: {at: number; length: number}[] = [];
    private finalRate = 0;
    private finishBy: number | null = null;
    private finished: (() => void)[] = [];

    constructor(private publish: (text: string) => void) {}

    start() {
        this.cancel();
    }

    private observeArrival(now: number, length: number) {
        this.arrivals.push({at: now, length});
        // A rolling four-second window follows changing provider speed without
        // mistaking the first buffered prefix or unchanged polls for throughput.
        while (this.arrivals.length > 2 && this.arrivals[1].at < now - 4000) this.arrivals.shift();
        const first = this.arrivals[0];
        if (now - first.at < 250) return;
        this.incomingRate = (length - first.length) / ((now - first.at) / 1000);
        const gaps = this.arrivals.slice(1).map((item, i) => (item.at - this.arrivals[i].at) / 1000).sort((a, b) => a - b);
        // Patch previews can arrive less often than Easy chunks. Retain enough
        // text for a typical gap, but don't make one network stall a huge delay.
        const typicalGap = gaps[Math.floor((gaps.length - 1) * 0.8)];
        this.reserveSeconds = Math.max(MIN_BUFFER_SECONDS, Math.min(2.5, typicalGap * 1.1));
    }

    append(text: string) {
        // Only a terminal fallback may replace a previously received prefix.
        if (this.finishBy !== null || text === this.target || !text.startsWith(this.target)) return;
        const now = performance.now();
        this.observeArrival(now, text.length);
        this.target = text;
        if (this.bufferUntil === null) this.bufferUntil = now + START_BUFFER_MS;
        this.schedule();
    }

    finish(text: string): Promise<void> {
        if (!text.startsWith(this.target)) {
            this.cancel();
            this.publish(text);
            return Promise.resolve();
        }
        if (text === this.visible) return Promise.resolve();
        this.target = text;
        if (this.finishBy === null) {
            const now = performance.now();
            this.finishBy = now + MAX_FINISH_MS;
            this.bufferUntil = now;
            // Once the result is complete there is no next packet to buffer.
            // Catch up in about one second instead of holding ready text for
            // a document-length timer, including a no-edit/final-only response.
            this.finalRate = Math.max(this.incomingRate, (text.length - this.visible.length) / 0.8);
        }
        return new Promise(resolve => {
            this.finished.push(resolve);
            this.schedule();
        });
    }

    cancel() {
        if (this.timer !== null) clearTimeout(this.timer);
        this.timer = null;
        this.target = this.visible = '';
        this.bufferUntil = this.finishBy = null;
        this.credit = this.rate = this.finalRate = 0;
        this.incomingRate = INITIAL_CHARS_PER_SECOND;
        this.reserveSeconds = MIN_BUFFER_SECONDS;
        this.arrivals = [];
        this.resolveFinished();
    }

    private resolveFinished() {
        const callbacks = this.finished;
        this.finished = [];
        callbacks.forEach(resolve => resolve());
    }

    private schedule() {
        if (this.timer !== null) return;
        this.lastTick = performance.now();
        this.timer = setTimeout(this.tick, 16);
    }

    private tick = () => {
        this.timer = null;
        const now = performance.now();
        const elapsed = Math.min(0.1, Math.max(0, (now - this.lastTick) / 1000));
        this.lastTick = now;
        if (now >= (this.bufferUntil ?? now)) {
            const remaining = this.target.length - this.visible.length;
            // Slow down as the queue shrinks, preserving some text across poll
            // jitter. New packets extend the queue without restarting animation.
            const desiredRate = this.finishBy === null
                ? Math.min(this.incomingRate * 0.95, remaining / this.reserveSeconds)
                : this.finalRate;
            this.rate += (desiredRate - this.rate) * (1 - Math.exp(-elapsed / 0.2));
            this.credit += this.rate * elapsed;
            let end = Math.min(this.target.length, this.visible.length + Math.floor(this.credit));
            if (this.finishBy !== null && now >= this.finishBy) end = this.target.length;
            // Never cut a UTF-16 surrogate pair (emoji/non-BMP letters).
            if (end < this.target.length && /[\uDC00-\uDFFF]/.test(this.target[end])) end++;
            if (end > this.visible.length) {
                this.credit = Math.max(0, this.credit - (end - this.visible.length));
                this.visible = this.target.slice(0, end);
                this.publish(this.visible);
            }
        }
        if (this.visible.length < this.target.length) {
            this.timer = setTimeout(this.tick, 16);
        } else {
            this.credit = 0;
            if (this.finishBy !== null) this.resolveFinished();
        }
    };
}
